import { useState, useEffect } from "react";
import axios from "../customAxios.js";
import {
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Box,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { get_full_address } from "../helpers/addressParser.js";
import {
  get_order_type_label,
  get_order_status_label,
} from "../helpers/enums.js";
import { strDateFormat } from "../utils/functions";
import ProcessProduct from "../components/ProcessProduct.jsx";

function ProcessOrder() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState("");

  const [selectedProductLocations, setSelectedProductLocations] = useState([]);

  let navigate = useNavigate();

  const fetchOrder = async () => {
    try {
      const { data: order } = await axios.get(`order_detail/${orderId}/`);
      setOrder(order);
      setLoadingError("");
    } catch (error) {
      console.error(`No se pudo obtener la orden: ${error}`);
      setLoadingError("No se pudo obtener la orden");
    }
  };

  const handleSetFirstProductLocations = (warehouseAssignments) => {
    const transformedData = {};

    // Iterar sobre cada elemento del objeto inicial
    for (const productId in warehouseAssignments) {
      if (Object.hasOwnProperty.call(warehouseAssignments, productId)) {
        const product = warehouseAssignments[productId];
        const updatedProduct = {
          slots: {},
          total_assigned: 0,
          total_stock: product.total_stock,
        };

        // Verificar si la propiedad "slots" existe en el producto
        if (Object.hasOwnProperty.call(product, "slots")) {
          updatedProduct.slots = { ...product.slots };

          // Iterar sobre cada ranura del producto
          for (const slotId in updatedProduct.slots) {
            if (Object.hasOwnProperty.call(updatedProduct.slots, slotId)) {
              updatedProduct.slots[slotId].assigned = 0;
              updatedProduct.slots[slotId].verified = 0;
            }
          }
        }

        transformedData[productId] = updatedProduct;
      }
    }
    setSelectedProductLocations(transformedData);
  };

  const fetchWarehouseAssignments = async () => {
    try {
      const { data } = await axios.get(
        `warehouses/warehouse_assignments_filtered/${orderId}/`
      );
      handleSetFirstProductLocations(data);
      setLoadingError("");
    } catch (error) {
      console.error(`Error al obtener las asignaciones de bodega: ${error}`);
      setLoadingError("Error al obtener las asignaciones de bodega");
    }
  };

  useEffect(() => {
    setLoading(true);

    Promise.all([fetchWarehouseAssignments(), fetchOrder()])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        // Manejar errores si alguna de las promesas falla
        console.error("Error:", error);
        setLoading(false);
        setLoadingError("Ocurrió un error al cargar la orden");
      });
  }, []);

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSuccessAlert(false);
    setOpenErrorAlert(false);
  };

  const handleProcessOrder = async () => {
    for (const product of order.products) {
      if (
        selectedProductLocations[product.product.id].total_assigned <
        product.quantity
      ) {
        setMessageAlert(
          "No se han asignado suficientes productos para " +
            product.product.name
        );
        setOpenErrorAlert(true);
        return;
      }

      const slots = Object.values(
        selectedProductLocations[product.product.id].slots
      );
      if (slots.some((slot) => slot.verified < slot.assigned)) {
        setMessageAlert(
          "No se han verificado los códigos de barras de todos los " +
            product.product.name
        );
        setOpenErrorAlert(true);
        return;
      }
    }
    const data = {
      order_id: orderId,
      products: order.products.map((product) => {
        return {
          product_id: product.product.id,
          slots: Object.keys(
            selectedProductLocations[product.product.id].slots
          ).map((slotId) => {
            const slot =
              selectedProductLocations[product.product.id].slots[slotId];
            return {
              slot_id: slotId,
              assigned_quantity: slot.assigned,
            };
          }),
        };
      }),
    };

    try {
      await axios.put("process_order/", data);
      setMessageAlert("Orden procesada correctamente");
      setOpenSuccessAlert(true);
      let labelUrl = order.delivery ? order.delivery.label : null;
      navigate("/home/process-success", { state: { labelUrl } });
    } catch (error) {
      console.error(`Error: ${error}`);
      setMessageAlert("Ocurrió un error al procesar la orden");
      setOpenErrorAlert(true);
    }
  };

  return loading ? (
    <CircularProgress className="loading-btn" />
  ) : loadingError !== "" ? (
    <Alert severity="error">{loadingError}</Alert>
  ) : (
    <>
      <div style={{ padding: "20px" }}>
        {/* Parte Superior */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <Button
            variant="outlined"
            style={{ marginRight: "10px" }}
            onClick={() => navigate(-1)}
          >
            ← Regresar
          </Button>
          <div>
            <Typography variant="h5">
              {order.seller.company.social_reason}
            </Typography>
            <Typography variant="h6">{order.reference_id}</Typography>
          </div>
        </div>

        {/* Etiquetas de Tipo y Estado */}
        <div style={{ marginBottom: "10px" }}>
          <Chip
            variant="contained"
            label={get_order_type_label(order.type)}
            style={{ marginRight: "10px" }}
          />
          <Chip
            variant="contained"
            label={get_order_status_label(order.mixed_status, order.type)}
            style={{ marginRight: "10px" }}
          />
          <Chip
            variant="contained"
            label={order.processable ? "Disponible" : "No disponible"}
            className={
              order.processable ? "processable-chip" : "unprocessable-chip"
            }
          />
        </div>
        <div style={{ marginBottom: "20px" }}>
          <Typography variant="h7">
            {"Fecha de Ingreso: " + strDateFormat(new Date(order.created_at))}
          </Typography>
        </div>

        <Box sx={{ width: "100%" }}>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            <Grid item xs={7}>
              {/* Información de Productos */}
              <Grid
                container
                rowSpacing={2}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              >
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <div style={{ marginBottom: "20px" }}>
                        <Typography variant="h5">Productos</Typography>
                      </div>
                      <Grid
                        container
                        rowSpacing={2}
                        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                      >
                        {order.products.map((item) => (
                          <ProcessProduct
                            orderProduct={item}
                            setSelectedProductLocations={
                              setSelectedProductLocations
                            }
                            selectedProductLocations={selectedProductLocations}
                          />
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={5}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              >
                <Grid item xs={12}>
                  <Card variant="outlined" style={{ marginBottom: "20px" }}>
                    <CardContent>
                      <Grid
                        container
                        rowSpacing={1}
                        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Grid item xs={12}>
                          <Typography variant="h6" className="text-bold">
                            Destinatario
                          </Typography>
                          <Typography>{order.recipient}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h6" className="text-bold">
                            Dirección
                          </Typography>
                          <Typography>
                            {order.delivery?.shipping_address
                              ? get_full_address(
                                  order.delivery?.shipping_address
                                )
                              : "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h6" className="text-bold">
                            Notas
                          </Typography>
                          <Typography>
                            {order.notes ? order.notes : "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h6" className="text-bold">
                            Mail contacto
                          </Typography>
                          <Typography>{order.contact_email}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h6" className="text-bold">
                            Teléfono contacto
                          </Typography>
                          <Typography>{order.contact_phone}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Grid container rowSpacing={2}>
                <Grid item xs={12}>
                  <Tooltip
                    title="No se puede procesar por falta de etiqueta. Revisar dirección"
                    disableHoverListener={
                      order.delivery?.label || !order.delivery
                    }
                  >
                    <div style={{ width: "100%" }}>
                      <Button
                        variant={
                          order.delivery && !order.delivery.label
                            ? "secondary"
                            : "contained"
                        }
                        disabled={order.delivery && !order.delivery.label}
                        onClick={handleProcessOrder}
                      >
                        Procesar Orden
                      </Button>
                    </div>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </div>
      <Snackbar
        open={openSuccessAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          {messageAlert}
        </Alert>
      </Snackbar>
      <Snackbar
        open={openErrorAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {messageAlert}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ProcessOrder;
